import { initializeApp, getApps, getApp } from "firebase/app";
import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";

const firebaseConfigs = {
  staging: {
    projectId: "everforge-fitness-staging",
    appId: "1:48697165878:web:ef990a1b5fea9945bcb097",
    storageBucket: "everforge-fitness-staging.firebasestorage.app",
    apiKey: "AIzaSyB2NeLA_O8tQM2n9CLCCR5So4NLTcd7pTU",
    authDomain: "everforge-fitness-staging.firebaseapp.com",
    messagingSenderId: "48697165878",
  },
  production: {
    projectId: "everforge-fitness-tracker",
    appId: "1:1068713356422:web:7002e5f7b511578de5f507",
    storageBucket: "everforge-fitness-tracker.firebasestorage.app",
    apiKey: "AIzaSyCNpHhxcmwDJD-IpVHr6BoOkJDm1A0nWO4",
    authDomain: "everforge-fitness-tracker.firebaseapp.com",
    messagingSenderId: "1068713356422",
  },
};

function resolveConfig() {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (
      host.includes("staging") ||
      host.includes("localhost") ||
      host === "127.0.0.1"
    ) {
      return firebaseConfigs.staging;
    }
    if (
      host === "everforgefitness.com" ||
      host === "www.everforgefitness.com" ||
      host === "everforge.web.app" ||
      host === "everforge-fitness-tracker.web.app"
    ) {
      return firebaseConfigs.production;
    }
  }

  return (import.meta as any).env?.VITE_APP_ENV === "production"
    ? firebaseConfigs.production
    : firebaseConfigs.staging;
}

let dbInstance: any = null;
function getDb() {
  if (dbInstance) return dbInstance;
  try {
    const config = resolveConfig();
    const app = getApps().length > 0 ? getApp() : initializeApp(config);
    dbInstance = getFirestore(app);
    return dbInstance;
  } catch (err) {
    console.warn("[OfferService] Error initializing Firebase:", err);
    return null;
  }
}

export interface PrivateOfferProposal {
  token: string;
  leadId: string;
  prospectName: string;
  prospectEmail: string;
  planId: string;
  planName: string;
  billingCycleMonths: number;
  baseMonthlyRate: number;
  durationDiscountPercent: number;
  festivalDiscountPercent: number;
  individualDiscountType: 'percent' | 'flat' | 'none';
  individualDiscountValue: number;
  discountReason?: string;
  finalMonthlyRate: number;
  totalCommitmentPrice: number;
  inclusions?: string[];
  status: 'active' | 'accepted' | 'expired' | 'revoked';
  createdAt: string;
  expiresAt: string;
  acceptedAt?: string;
}

/**
 * Fetch and validate private offer by token from Firestore.
 */
export async function getPrivateOfferByToken(token: string): Promise<PrivateOfferProposal | null> {
  const db = getDb();
  if (!db) return null;

  try {
    const offerRef = doc(db, "privateOffers", token);
    const snap = await getDoc(offerRef);
    if (!snap.exists()) {
      return null;
    }

    const data = snap.data() as PrivateOfferProposal;
    // Check expiration
    if (data.expiresAt && new Date(data.expiresAt).getTime() < Date.now()) {
      return { ...data, status: 'expired' };
    }
    return data;
  } catch (err) {
    console.error("[OfferService] Failed to load offer:", err);
    return null;
  }
}

/**
 * Record prospect acceptance of the private offer proposal.
 */
export async function acceptPrivateOffer(token: string): Promise<boolean> {
  const db = getDb();
  if (!db) return false;

  try {
    const offerRef = doc(db, "privateOffers", token);
    const now = new Date().toISOString();
    await setDoc(
      offerRef,
      {
        status: "accepted",
        acceptedAt: now,
      },
      { merge: true }
    );
    return true;
  } catch (err) {
    console.error("[OfferService] Failed to record acceptance:", err);
    return false;
  }
}

